// Signed-in onboarding continuation: "Create your depot" → spinner → ready.
// Drives the adapter's POST /auth/me/provision endpoint, which links a real
// pool depot to the session's user. The endpoint returns:
//   200 {"depotKey":"3-X0663431-2"}  — depot linked, done
//   202 (empty body)                 — CORE user still provisioning, retry
// We keep a spinner and retry on 202 every ~2.5s, capped at ~16 tries (~40s),
// then surface the ready state with a "Continue to dashboard" hand-off.

import { useState } from "react";
import { IdentityHeader } from "./IdentityHeader";
import { brandLabel, surfaceUrl, type Brand } from "./brand";
import type { BffClaims } from "@mf/auth-client";

const POLL_INTERVAL_MS = 2500;
const MAX_ATTEMPTS = 16; // ~16 × 2.5s ≈ 40s

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

interface ProvisionResponse {
  depotKey: string;
}

// POSTs /auth/me/provision, retrying while the adapter answers 202 (CORE user
// not yet provisioned). Resolves with the depotKey once it answers 200.
async function provisionDepot(): Promise<string> {
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    const res = await fetch("/api/mf-auth-adapter/auth/me/provision", {
      method: "POST",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (res.status === 202) {
      // Still in flight — wait and retry.
      await sleep(POLL_INTERVAL_MS);
      continue;
    }
    if (!res.ok) throw new Error(`provision_failed_${res.status}`);
    const body = (await res.json()) as ProvisionResponse;
    return body.depotKey;
  }
  throw new Error("provision_timeout");
}

type Phase =
  | { kind: "idle" }
  | { kind: "creating" }
  | { kind: "ready"; depotKey: string }
  | { kind: "error"; reason: string };

interface DepotProvisioningProps {
  session: BffClaims;
  brand: Brand;
}

export function DepotProvisioning({ session, brand }: DepotProvisioningProps) {
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const onlineUrl = surfaceUrl("online", brand);

  async function onCreate() {
    setPhase({ kind: "creating" });
    try {
      const depotKey = await provisionDepot();
      setPhase({ kind: "ready", depotKey });
    } catch (err) {
      const reason = err instanceof Error ? err.message : "provision_failed";
      setPhase({ kind: "error", reason });
    }
  }

  return (
    <>
      <IdentityHeader email={session.email} sub={session.sub} />
      <header className="hub-page-header">
        <h1>You&apos;re signed in</h1>
        <p>
          Your {brandLabel(brand)} account is ready. Create your depot to finish
          onboarding, then continue to your {brandLabel(brand)} dashboard.
        </p>
      </header>

      {phase.kind === "ready" ? (
        <section className="hub-depot-ready" data-hub-depot-ready>
          <span className="hub-check" aria-hidden="true">
            ✓
          </span>
          Your depot <code>{phase.depotKey}</code> is ready.
        </section>
      ) : null}

      {phase.kind === "error" ? (
        <section className="hub-error-banner" data-hub-provision-error>
          We couldn&apos;t create your depot ({phase.reason}). Please try again
          in a moment.
        </section>
      ) : null}

      <main className="hub-cta-row">
        {phase.kind === "ready" ? (
          <a className="hub-cta-button" href={onlineUrl} data-hub-continue>
            Continue to dashboard
          </a>
        ) : (
          <button
            type="button"
            className="hub-cta-button"
            data-hub-create-depot
            disabled={phase.kind === "creating"}
            onClick={onCreate}
          >
            {phase.kind === "creating" ? (
              <>
                <span className="hub-spinner" aria-hidden="true" />
                Creating your depot…
              </>
            ) : phase.kind === "error" ? (
              "Try again"
            ) : (
              "Create your depot"
            )}
          </button>
        )}
      </main>
    </>
  );
}
